/**
 * Pre-insert parking-spot-occupancy guard.
 *
 * Returns true when the candidate parking spot already holds an active ticket
 * on the same scheduled_date. The caller (OptiPlaceModal parked-on-arrival path,
 * and any future place-into-spot site) uses this to fail with a clear
 * "spot occupied" message BEFORE the insert, instead of letting the
 * `uniq_active_parking_spot` partial unique index raise a raw 23505.
 *
 * Mirrors the WHERE clause of the partial unique index defined in
 * `supabase/migrations/20260512190000_uniq_active_bay_per_day.sql` (the parking
 * spot key shape):
 *
 *   parking_spot_id IS NOT NULL
 *     AND picked_up_at IS NULL
 *     AND rollover_of_ticket_id IS NULL
 *     AND status IN ('Active','Paused','Completed','Scheduled','Pending')
 *     -- key shape: (parking_spot_id, scheduled_date)
 *
 * NOTE the "active occupancy" definition differs from `uniq_active_bay`: a spot
 * stays held by a Completed-but-not-yet-picked-up ticket (parking-lot immunity),
 * and is freed by `picked_up_at` rather than by status alone. Cancelled/Deleted
 * tickets are NOT in the status list, so they free the spot. The JS-side check
 * is exact w.r.t. the constraint.
 *
 * Boundary: this only INSPECTS state. It does not write or assign — the caller
 * decides how to react (typically: throw a user-facing "spot occupied" error).
 */

/** Ticket statuses that count as occupying a parking spot (mirrors the index). */
export const ACTIVE_PARKING_SPOT_STATUSES = [
  'Active',
  'Paused',
  'Completed',
  'Scheduled',
  'Pending',
] as const;

// The guard needs the real client's `.from`. Matching the concrete client
// against a hand-rolled structural chain type expands the postgrest builder's
// generics and hits TS2589, so depend on the real client type directly (its
// concrete-literal query below type-checks the same way as everywhere else).
import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@/integrations/supabase/types';

type SupabaseLike = Pick<SupabaseClient<Database>, 'from'>;

/**
 * Flat result shape. A boolean-discriminated union (`ok: true | false`) does
 * NOT narrow under this project's `strictNullChecks: false` config, so callers
 * couldn't reach `.error`/`.occupied` after an `if (!res.ok)` check. Optional
 * fields on a single shape read safely regardless of `ok` and need no
 * narrowing: `occupied` is set on success, `error` on failure.
 */
export interface SpotOccupancyResult {
  ok: boolean;
  occupied?: boolean;
  error?: unknown;
}

export interface IsSpotCurrentlyOccupiedArgs {
  supabase: SupabaseLike;
  parkingSpotId: string;
  scheduledDate: string;
  /** Shop scope (defense-in-depth; a spot id already belongs to one shop). */
  shopId: string;
  /**
   * The new ticket's id. Excluded from the query so re-running the guard for an
   * in-flight insert/retry — or re-saving a ticket that keeps its OWN spot —
   * doesn't false-positive on the row this caller is about to write/own.
   */
  excludeTicketId?: string | null;
}

export async function isSpotCurrentlyOccupied({
  supabase,
  parkingSpotId,
  scheduledDate,
  shopId,
  excludeTicketId,
}: IsSpotCurrentlyOccupiedArgs): Promise<SpotOccupancyResult> {
  let q = supabase
    .from('tickets')
    .select('id')
    .eq('parking_spot_id', parkingSpotId)
    .eq('scheduled_date', scheduledDate)
    .eq('shop_id', shopId)
    .is('picked_up_at', null)
    .is('rollover_of_ticket_id', null)
    .in('status', [...ACTIVE_PARKING_SPOT_STATUSES]);
  if (excludeTicketId) {
    q = q.neq('id', excludeTicketId);
  }
  const { data, error } = await q.limit(1);
  if (error) {
    return { ok: false, error };
  }
  return { ok: true, occupied: Array.isArray(data) && data.length > 0 };
}
